import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
} from "@material-ui/core";
import {
  fetchOdkUser,
  fetchAccreditationDetailForms,
  fetchQADetailForms,
  assignFormsToOdkUser,
  fetchUserQrCode,
  UnassignFormsToOdkUser,
} from "../actions";
import { connect } from "react-redux";
import { injectIntl } from "react-intl";
import { withTheme, withStyles } from "@material-ui/core/styles";
import { withModulesManager, journalize } from "@openimis/fe-core";
import { bindActionCreators } from "redux";
import QRCodeGenerator from "./QRCodeGenerator";
import AssignFormsUserList from "./AssignFormsUserList";
import UnAssignFormsUserList from "./UnAssignFormsUserList"; // Add this import
import { DETAIL_TYPE_ACC, DETAIL_TYPE_QA } from "../constants";
import { convertJsonToQRcode } from "../helpers";

const styles = (theme) => ({
  title: theme.paper.title,
  errorMessage: {
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3),
  },
});

function OdkUsersList(props) {
  const { classes, odkUsers, fetchingODKUsers, fetchedODKUsers, errorODKUsers, detailUuid, projectId } = props;

  const [openQRCodeModal, setOpenQRCodeModal] = useState(false);
  const [selectedQRCode, setSelectedQRCode] = useState("");

  const [openAssignFormModal, setOpenAssignFormModal] = useState(false);
  const [openUnassignFormModal, setOpenUnassignFormModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    console.log("Fetching ODK Users with:", detailUuid, props.detailType);
    if (detailUuid) {
      props.fetchOdkUser(detailUuid, props.detailType).catch((err) => handleError("Failed to fetch ODK users", err));
    }
  }, [detailUuid, props.detailType]);

  const handleError = (message, err) => {
    console.error(`${message}:`, err);
    setError(message);
    setLoading(false);
  };

  const handleViewQRCode = (qrCodeJson) => {
    try {
      if (!qrCodeJson) {
        throw new Error("QR Code data is missing");
      }

      const jsonString = qrCodeJson.replace(/'/g, '"').replace(/\bFalse\b/g, "false");
      const parsedJson = JSON.parse(jsonString);
      const formattedQRCode = convertJsonToQRcode(parsedJson);

      setSelectedQRCode(formattedQRCode);
      setOpenQRCodeModal(true);
    } catch (err) {
      handleError("Failed to generate QR Code", err);
    }
  };

  const handleCloseQRCodeModal = () => {
    setOpenQRCodeModal(false);
  };

  const handleAssignForm = (user) => {
    try {
      setLoading(true);

      if (!user) throw new Error("No user selected");
      if (!user.uuid) throw new Error("User has no UUID");
      if (!detailUuid) throw new Error("Detail UUID is missing");
      if (!projectId) throw new Error("Project ID is missing");

      setSelectedUser(user);
      setOpenAssignFormModal(true);
      setLoading(false);
    } catch (err) {
      handleError("Cannot open assign form modal", err);
    }
  };

  const handleUnassignForm = (user) => {
    try {
      if (!user || !user.uuid) {
        throw new Error("Invalid user for unassign action");
      }

      setSelectedUser(user);
      setOpenUnassignFormModal(true); // <-- Open the unassign modal
    } catch (err) {
      handleError("Failed to unassign form", err);
    }
  };

  const handleCloseAssignFormModal = () => {
    setOpenAssignFormModal(false);
  };

  const handleCloseUnassignFormModal = () => {
    setOpenUnassignFormModal(false);
  };

  const renderUserInfo = (user) => {
    if (!user) return "Unknown User";

    const username = user.username || "";
    const otherNames = user.otherNames || "";
    const lastName = user.lastName || "";

    return `${username} ${otherNames} ${lastName}`.trim() || "Unknown User";
  };

  return (
    <Box mt={3}>
      <Typography className={classes.title}>
        {fetchingODKUsers ? "Loading Users..." : errorODKUsers ? "Error Fetching Users" : "User List"}
      </Typography>

      {error && <Typography className={classes.errorMessage}>{error}</Typography>}

      {loading && (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      )}

      {fetchedODKUsers && Array.isArray(odkUsers) && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="center">Assign Form to User</TableCell>
                <TableCell align="center">QRCode</TableCell>
                <TableCell align="center">Unassign Form from User</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {odkUsers.length > 0 ? (
                odkUsers.map((row) => (
                  <TableRow key={row.uuid || `row-${Math.random()}`}>
                    <TableCell>{renderUserInfo(row.user)}</TableCell>
                    <TableCell align="center">
                      <Button
                        variant="outlined"
                        color="primary"
                        disabled={loading}
                        onClick={() => handleAssignForm(row)}
                      >
                        Assign Form
                      </Button>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant="outlined"
                        color="primary"
                        disabled={!row.qrCodeJson || loading}
                        onClick={() => handleViewQRCode(row.qrCodeJson)}
                      >
                        View QRCode
                      </Button>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant="outlined"
                        color="primary"
                        disabled={loading}
                        onClick={() => handleUnassignForm(row)}
                      >
                        Unassign Form
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {openQRCodeModal && (
        <Dialog open={openQRCodeModal} onClose={handleCloseQRCodeModal} maxWidth="sm" fullWidth>
          <DialogContent>
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="200px">
              {selectedQRCode ? (
                <QRCodeGenerator value={selectedQRCode} size={500} />
              ) : (
                <Typography>No QR Code Available</Typography>
              )}
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {openAssignFormModal && selectedUser && selectedUser.uuid && detailUuid && projectId && (
        <Dialog open={openAssignFormModal} onClose={handleCloseAssignFormModal} maxWidth="sm" fullWidth>
          <DialogTitle>Assign Forms to {selectedUser.user ? renderUserInfo(selectedUser.user) : "User"}</DialogTitle>
          <DialogContent>
            <AssignFormsUserList
              projectId={projectId}
              detailUuid={detailUuid}
              userUuid={selectedUser.uuid}
              detailType={props.detailType}
            />
          </DialogContent>
        </Dialog>
      )}

      {openUnassignFormModal && selectedUser && selectedUser.uuid && detailUuid && projectId && (
        <Dialog open={openUnassignFormModal} onClose={handleCloseUnassignFormModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            Unassign Forms from {selectedUser.user ? renderUserInfo(selectedUser.user) : "User"}
          </DialogTitle>
          <DialogContent>
            <UnAssignFormsUserList
              projectId={projectId}
              detailUuid={detailUuid}
              userUuid={selectedUser.uuid}
              detailType={props.detailType}
            />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
}

const mapStateToProps = (state) => {
  console.log("State", state);
  return {
    rights: !!state.core && !!state.core.user && !!state.core.user.i_user ? state.core.user.i_user.rights : [],
    mutation: state.quality_assurance_and_accreditation.mutation,
    submittingMutation: state.quality_assurance_and_accreditation.submittingMutation,
    odkUsers: state.quality_assurance_and_accreditation.odkUsers || [],
    fetchingODKUsers: state.quality_assurance_and_accreditation.fetchingODKUsers,
    fetchedODKUsers: state.quality_assurance_and_accreditation.fetchedODKUsers,
    errorODKUsers: state.quality_assurance_and_accreditation.errorODKUsers,
    accreditationDetailsForms: state.quality_assurance_and_accreditation.accreditationDetailsForms,
    fetchingAccreditationDetailsForms: state.quality_assurance_and_accreditation.fetchingAccreditationDetailsForms,
    fetchedAccreditationDetailsForms: state.quality_assurance_and_accreditation.fetchedAccreditationDetailsForms,
    errorAccreditationDetailsForms: state.quality_assurance_and_accreditation.errorAccreditationDetailsForms,
    QRCode: state.quality_assurance_and_accreditation.QRCode,
    fetchingQRCode: state.quality_assurance_and_accreditation.fetchingQRCode,
    fetchedQRCode: state.quality_assurance_and_accreditation.fetchedQRCode,
    errorQRCode: state.quality_assurance_and_accreditation.errorQRCode,
    qaDetailsForms: state.quality_assurance_and_accreditation.qaDetailsForms,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      journalize,
      fetchOdkUser,
      fetchAccreditationDetailForms,
      fetchQADetailForms,
      fetchUserQrCode,
      assignFormsToOdkUser,
      UnassignFormsToOdkUser,
    },
    dispatch,
  );
};

export default withModulesManager(
  connect(mapStateToProps, mapDispatchToProps)(injectIntl(withTheme(withStyles(styles)(OdkUsersList)))),
);
